import {
    StyledAgendaBlockoutEvent,
    StyledAgendaUnavailabilityEvent
} from '@/features/agenda/components/agenda-blockout-event/style'
import {useDateValidation} from '@/features/agenda/hooks/useDateValidation'
import {absenceTypeToLabel} from '@/features/agenda/utils'
import {useTranslation} from '@/translations/i18n'
import {useDialogStore} from '@ariakit/react'
import {DeletePatientAbsenceDialog} from '../delete-patient-absence-dialog/DeletePatientAbsenceDialog'
import {useState, useCallback} from 'react'
import {DeleteConsultantAbsenceDialog} from '../delete-consultant-absence-dialog/DeleteConsultantAbsenceDialog'
import type {AgendaEventAbsence, AgendaEventUnavailability} from '@/features/agenda/types'
import {ContextMenu} from '@/components/commons/context-menu/ContextMenu'
import {AgendaBlockoutEventContextMenu} from '@/features/agenda/components/agenda-blockout-event-context-menu/AgendaBlockoutEventContextMenu'
import {AgendaCreateEventContextMenuDialog} from '@/features/agenda/components/agenda-appointment-event-context-menu/AgendaCreateEventContextMenuDialog'
import dayjs from 'dayjs'

export const AgendaBlockoutEvent = ({event}: {event: AgendaEventAbsence | AgendaEventUnavailability}) => {
    const {t} = useTranslation()
    const {isDisabledDay} = useDateValidation()
    const [isDeletePatientAbsenceDialogOpen, setIsDeletePatientAbsenceDialogOpen] = useState(false)
    const deletePatientAbsenceDialogStore = useDialogStore({
        open: isDeletePatientAbsenceDialogOpen,
        setOpen: setIsDeletePatientAbsenceDialogOpen
    })
    const [isDeleteConsultantAbsenceDialogOpen, setIsDeleteConsultantAbsenceDialogOpen] = useState(false)
    const deleteConsultantAbsenceDialogStore = useDialogStore({
        open: isDeleteConsultantAbsenceDialogOpen,
        setOpen: setIsDeleteConsultantAbsenceDialogOpen
    })

    const createEventDialogStore = useDialogStore()

    const handleClickContextMenuItem = useCallback(
        (action: 'deleteAbsence' | 'addEvent') => {
            if (action == 'deleteAbsence') {
                if (event.type == 'patient_absence') {
                    deletePatientAbsenceDialogStore.setOpen(true)
                } else if (event.type == 'consultant_absence') {
                    deleteConsultantAbsenceDialogStore.setOpen(true)
                }
            }

            if (action == 'addEvent') {
                createEventDialogStore.show()
            }
        },
        [event.type, deletePatientAbsenceDialogStore, deleteConsultantAbsenceDialogStore, createEventDialogStore]
    )

    const renderMenu = useCallback(
        ({anchorRect}: {anchorRect: {x: number; y: number}}) => (
            <AgendaBlockoutEventContextMenu anchorRect={anchorRect} onChange={handleClickContextMenuItem} />
        ),
        [handleClickContextMenuItem]
    )

    if (isDisabledDay(event.start)) {
        return null
    }

    // We know at this point that event.type is either 'patient_absence' or 'consultant_absence' or 'unavailability'
    const blockoutType = event.type

    if (!blockoutType) {
        return <StyledAgendaUnavailabilityEvent />
    }

    if (blockoutType == 'unavailability') {
        return <StyledAgendaUnavailabilityEvent />
    }

    return (
        <>
            <ContextMenu menu={renderMenu} style={{height: '100%'}}>
                <StyledAgendaBlockoutEvent className="blockout-event" $absenceType={blockoutType}>
                    {t(absenceTypeToLabel[blockoutType])}
                </StyledAgendaBlockoutEvent>
            </ContextMenu>
            {isDeletePatientAbsenceDialogOpen && (
                <DeletePatientAbsenceDialog event={event} dialogStore={deletePatientAbsenceDialogStore} />
            )}
            {isDeleteConsultantAbsenceDialogOpen && (
                <DeleteConsultantAbsenceDialog event={event} dialogStore={deleteConsultantAbsenceDialogStore} />
            )}
            <AgendaCreateEventContextMenuDialog
                start={event.start}
                end={dayjs(event.start).add(1, 'hour').toDate()}
                createEventDialogStore={createEventDialogStore}
            />
        </>
    )
}
